import React from 'react';

import BlockFormNavigation from '@dashboard/components/BlockPageNavigation/BlockFormNavigation';
import Form from '@dashboard/components/form/Form';
import FormPageNavBar from '@dashboard/components/FormPageNavBar';
import PlatformSendingStatusAlert from '@dashboard/components/PlatformSendingStatusAlert';
import { type UpdateWorkflowMutationVariables, useUpdateWorkflowMutation } from '@dashboard/generated/wp/platform';
import useCurrentPlatformId from '@dashboard/hooks/route/useCurrentPlatformId';
import { Button, Grid } from '@mui/material';

import { FormContextProvider } from './FormContext';
import NavBarName from './NavBarName';
import StepsSection from './StepsSection';
import TestDataSection from './TestDataSection';
import TestWorkflowSection from './TestWorkflowSection';
import TriggersSection from './TriggersSection';
import UnsavedTriggersSectionAlert from './UnsavedTriggersSectionAlert';
import useInitialValues from './useInitialValues';
import WorkflowContextActionsMenu from './WorkflowContextActionsMenu';

type TForm = UpdateWorkflowMutationVariables;

export default function WorkflowEdit() {
  const [update] = useUpdateWorkflowMutation();
  const initialValues = useInitialValues();
  const platformId = useCurrentPlatformId();

  const onSubmitHandler = async (data: TForm) => {
    await update({
      variables: {
        id: data.id,
        stepDefinitions: data.stepDefinitions,
        triggers: data.triggers ?? [],
      },
    });
  };

  if (initialValues === null) {
    return null;
  }

  return (
    <>
      <PlatformSendingStatusAlert />
      <FormPageNavBar
        backHref={`/o/${platformId}/workflows`}
        leftChildren={<NavBarName />}
        rightChildren={
          <>
            <WorkflowContextActionsMenu />
            <Button form="wf-form" variant="contained" color="primary" type="submit">
              Save
            </Button>
          </>
        }
      />
      <Form id="wf-form" successMessage="Workflow saved" initialValues={initialValues} onSubmit={onSubmitHandler}>
        <BlockFormNavigation message="Are you sure you want to leave the workflow builder?" />

        <FormContextProvider>
          <Grid container justifyContent="center" sx={{ my: { xs: 1, md: 3 } }}>
            <Grid item xs={12} md={10} sx={{ mb: 5, maxWidth: { md: 880 } }}>
              <TriggersSection />
              <UnsavedTriggersSectionAlert />
              <TestDataSection />
              <StepsSection />
              <TestWorkflowSection />
            </Grid>
          </Grid>
        </FormContextProvider>
      </Form>
    </>
  );
}
